import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { Anchor, Box, Breadcrumbs, Text, useMantineColorScheme } from '@mantine/core'
import { useAuth } from '@/contexts/AuthContext'
import { useBreadcrumb } from '@/contexts/BreadcrumbContext'

type Segment = { label: string; to?: string }

const ENTITY_TYPE_KEYS: Record<string, string> = {
  municipality: 'entities.municipality_other',
  partner: 'entities.partner_other',
  organization: 'entities.organization_other',
}

const ENTITY_LIST_PATH: Record<string, string> = {
  municipality: '/entities/municipality',
  partner: '/entities/partner',
  organization: '/entities/organization',
}

export default function AppBreadcrumbs() {
  const location = useLocation()
  const { entityName } = useBreadcrumb()
  const { me } = useAuth()
  const { t } = useTranslation()
  const { colorScheme } = useMantineColorScheme()
  const isDark = colorScheme === 'dark'

  const path = location.pathname
  const isSelf = location.state?.self ?? false

  // Don't show on dashboard or root
  if (path === '/home' || path === '/') {
    return null
  }

  const segments: Segment[] = [{ label: t('breadcrumbs.dashboard'), to: '/home' }]

  // /entities/:entityType (list pages)
  const entitiesMatch = path.match(/^\/entities\/(\w+)/)
  if (entitiesMatch) {
    const entityType = entitiesMatch[1]
    const key = ENTITY_TYPE_KEYS[entityType]
    if (key) {
      segments.push({ label: t(key) })
    }
  }

  // /entity/:entityType/:entityId (detail pages)
  const entityMatch = path.match(/^\/entity\/(\w+)\/([^/]+)/)
  if (entityMatch) {
    const entityType = entityMatch[1]
    const entityId = entityMatch[2]
    const isOwnEntity = isSelf || (me?.scopeId && me.scopeId === entityId)

    if (isOwnEntity) {
      // "My Entity" flow — just show entity name, no list link
      segments.push({ label: entityName || t('dashboard.myEntity') })
    } else {
      // Navigated from a list — show entity type as link back to list
      const key = ENTITY_TYPE_KEYS[entityType]
      const listPath = ENTITY_LIST_PATH[entityType]
      if (key && listPath) {
        segments.push({ label: t(key), to: listPath })
      }
      if (entityName) {
        segments.push({ label: entityName })
      }
    }
  }

  // /me (profile)
  if (path === '/me' || path.startsWith('/me/')) {
    segments.push({ label: t('breadcrumbs.profile') })
  }

  // /my-documents
  if (path === '/my-documents') {
    segments.push({ label: t('breadcrumbs.my_documents') })
  }

  // Don't render if only dashboard segment
  if (segments.length <= 1) {
    return null
  }

  const accentColor = isDark ? '#00ff00' : '#00aa00'
  const textColor = isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'

  return (
    <Box
      px="lg"
      py={6}
      style={{
        borderBottom: `1px solid ${isDark ? 'rgba(0, 255, 0, 0.1)' : 'rgba(0, 100, 0, 0.08)'}`,
        background: isDark ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.6)',
      }}
    >
      <Breadcrumbs
        separator=">"
        styles={{
          separator: { color: textColor, marginInline: 8 },
        }}
      >
        {segments.map((seg, i) => {
          const isLast = i === segments.length - 1
          if (isLast || !seg.to) {
            return (
              <Text key={i} size="sm" c={isLast ? accentColor : textColor} fw={isLast ? 500 : 400}>
                {seg.label}
              </Text>
            )
          }
          return (
            <Anchor key={i} component={Link} to={seg.to} size="sm" c={textColor} underline="hover">
              {seg.label}
            </Anchor>
          )
        })}
      </Breadcrumbs>
    </Box>
  )
}
